function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import Service from '@ember/service';
import { assert } from '@ember/debug';
import { set, get } from '@ember/object';
import { DataType } from '@square/ember-features/protos/squareup/feature/relay/common/data_type';
import { fetchFlags, parseFlagTypes, parseFlagValues } from '@square/ember-features/utils/request';
import { formatLaunchDarklyFlagNameToFeatureService, formatToLaunchdarklyKeyIfNeeded } from '@square/ember-features/utils/feature-flag-name-format';
// This is a service which holds feature flag state. Feature flag state
// can be set on the service by an initializer.

// Currently the service is bootstrapped with flags sourced from experiments-relay
export default class FeaturesService extends Service {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "_flags", {});
    _defineProperty(this, "_flag_types", {});
    _defineProperty(this, "_isSetup", false);
    _defineProperty(this, "_observabilityAddFeatureFlagEvaluation", () => {});
  }
  // eslint-disable-line @typescript-eslint/no-empty-function

  /**
   * Helper for initializing the flags in the service.
   * @param {FlagValuesObject} flagValues - feature flag values
   * @param {?FlagTypesObject} flagTypes - feature flag types
   */
  setup(flagValues, flagTypes = {}) {
    assert('Feature flag state has already been setup', !this._isSetup);
    set(this, '_isSetup', true);
    for (const flagName in flagValues) {
      if (Object.prototype.hasOwnProperty.call(flagValues, flagName)) {
        this._setFlag(flagName, flagValues[flagName]);
      }
    }
    for (const flagName in flagTypes) {
      if (Object.prototype.hasOwnProperty.call(flagTypes, flagName)) {
        this._setFlagType(flagName, flagTypes[flagName]);
      }
    }
  }
  setupObservability(fn) {
    this._observabilityAddFeatureFlagEvaluation = fn;
  }

  /**
   * Method for evaluating boolean Launch Darkly flags, and then initializing the flags in the service.
   * @param {object} flags - object of flags, with names in feature service format (ex. group/feature_name)
   * @param {string} merchantToken - merchant token
   * @param {object} attributes - an object of attributes to use for feature flag evaluation (merchant country, product, etc.). This field is optional
   */
  async evaluateAndSetup(flags, merchantToken, attributes) {
    const evaluatedFeatureFlags = await this.evaluate(flags, merchantToken, attributes);
    this.setup(evaluatedFeatureFlags);
  }

  /**
   * Method for evaluating Launch Darkly flags
   * @param {object} flags - object of flags, with names in feature service format (ex. group/feature_name)
   * @param {string} merchantToken - merchant token
   * @param {object} attributes - an object of attributes to use for feature flag evaluation (merchant country, product, etc.). This field is optional
   * @param {boolean} setFlags - boolean flag to set new evaluated flags, this will be false by default and can only be true if the service is already initialized. This field is optional
   */
  async evaluate(flags, merchantToken, attributes, setFlags) {
    assert('there should be at least one feature flag passed in', flags && flags.length > 0);
    assert('merchantToken is required', merchantToken);
    if (setFlags) {
      assert('To set flags the service should be initialized. Please run setup or evaluateAndSetup', this._isSetup);
    }
    const response = await fetchFlags(flags, merchantToken, attributes);
    const flagValues = parseFlagValues(response);
    const flagTypes = parseFlagTypes(response);
    if (setFlags) {
      for (const flagName in flagValues) {
        if (Object.prototype.hasOwnProperty.call(flagValues, flagName)) {
          this._setFlag(flagName, flagValues[flagName]);
          this._setFlagType(flagName, flagTypes[flagName]);
        }
      }
    }
    return flagValues;
  }

  /**
   * Helper for checking the value of a boolean flag.
   * @argument {string} flag
   * @returns {boolean} value of the flag
   * */
  isEnabled(flag) {
    const flagValue = this.getFlag(flag);
    if (typeof flagValue !== 'boolean' && typeof flagValue !== 'undefined') {
      throw new TypeError(`'isEnabled' should only be used for boolean flags: ${flag}`);
    }
    return flagValue || false;
  }

  /**
   * Helper for checking the value of a flag.
   * @argument {string} flag
   * @returns {FlagValues} value of the flag
   * */
  getFlag(flag) {
    assert('Feature flag name must be a string', typeof flag === 'string');
    const returnValue = this._getLdFlag(flag);
    this._observabilityAddFeatureFlagEvaluation(flag, returnValue);
    return returnValue;
  }
  _getLdFlag(flag) {
    // Flags with incomplete roll outs must always be sourced from the midRolloutFlag set.
    // Those flags are still in the feature editor, so their name must be in the feature-editor format.
    const featureEditorName = formatLaunchDarklyFlagNameToFeatureService(flag);
    const ldKey = formatToLaunchdarklyKeyIfNeeded(flag);
    // Try with LD key first, then with the feature service key in case we are setup with an old name(tests do that).
    return get(this._flags, ldKey) || get(this._flags, featureEditorName);
  }
  getFlagType(flag) {
    assert('Feature flag name must be a string', typeof flag === 'string');
    const ldKey = formatToLaunchdarklyKeyIfNeeded(flag);
    const flagType = Object.prototype.hasOwnProperty.call(this._flag_types, ldKey) ? get(this._flag_types, ldKey) : DataType.BOOLEAN;
    return flagType;
  }

  /**
   * Helper for determining if the service has already been setup
   * @returns {boolean} whether the service has been setup or not.
   * */
  isSetup() {
    return this._isSetup;
  }
  _setFlag(flag, value) {
    this._ensureFlagValue(flag, value);
    this._setEmptyNests(this._flags, flag);
    set(this._flags, flag, value);
  }
  _setFlagType(flag, type) {
    this._ensureFlagType(flag, type);
    this._setEmptyNests(this._flag_types, flag);
    set(this._flag_types, flag, type);
  }
  _ensureFlagValue(flag, value) {
    assert('Feature flag name must be a string', typeof flag === 'string');
    assert(`Feature flag "${flag}" value must be undefined, boolean, number, object, or string; was given ${value}`, ['boolean', 'number', 'string', 'object', 'undefined'].includes(typeof value));
  }
  _ensureFlagType(flag, type) {
    assert('Feature flag name must be a string', typeof flag === 'string');
    assert('Feature flag type must be a boolean, integer, double, string, or json', [DataType.BOOLEAN, DataType.INTEGER, DataType.DOUBLE, DataType.STRING, DataType.JSON].includes(type));
  }
  _setEmptyNests(object, path) {
    const components = path.split('.');
    for (let i = 1; i < components.length; i++) {
      const key = components.slice(0, i).join('.');
      if (get(object, key) === undefined) {
        set(object, key, {});
      }
    }
  }

  /**
   * Test helper for resetting the service.
   * */
  reset() {
    set(this, '_flags', {});
    set(this, '_flag_types', {});
    set(this, '_isSetup', false);
  }
}

// This is how TypeScript knows how to look up the service.
/** @hidden */